import { useQuery } from "@tanstack/react-query"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { Avatar } from "@tokenterminal/core/ui/icon/Avatar"
import { Modal } from "@tokenterminal/core/ui/overlays/modal/Modal"
import { formatNumber } from "@tokenterminal/core/utils/sharedFormatter"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { useTimeseries } from "modules/projects/hooks/useTimeseries"
import Link from "next/link"
import { useMemo } from "react"
import { useTRPC } from "utils/trpc"

type ChainItem = {
  logo: string | null
  name: string
  slug: string
  chain_id: string
  value: number | null
}

export const ChainsDeployedOnModal = ({
  projectSlug,
  handleClose,
  metricId = "user_dau",
  shouldFilterSelf = true,
}: {
  projectSlug: string
  handleClose: () => void
  metricId?: string | null
  shouldFilterSelf?: boolean
}) => {
  const trpc = useTRPC()
  const { data: project } = useQuery(
    trpc.projects.getProject.queryOptions({
      slug: projectSlug,
    })
  )
  const { data: { data: chains = [] } = {} } = useQuery(
    trpc.chains.getChains.queryOptions()
  )
  const chainsById = useMemo(() => toDictionary(chains, "chain_id"), [chains])

  const { data: timeseriesData } = useTimeseries({
    dataIds: project ? [project.data_id] : [],
    metricIds: metricId ? [metricId] : [],
    interval: "7d",
    groupBy: "chain",
    enabled: !!project && !!metricId,
  })

  const latestTimeseriesData = useMemo(() => {
    if (!timeseriesData?.data) return {}

    // Group by chain_id
    const groupedByChain = timeseriesData.data
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )
      .reduce(
        (acc, item) => {
          if (item.chain_id && !acc[item.chain_id]) {
            acc[item.chain_id] = {
              chain_id: item.chain_id,
              value: item.value,
              timestamp: item.timestamp,
            }
          }
          return acc
        },
        {} as Record<
          string,
          { chain_id: string; value: number | null; timestamp: string }
        >
      )

    // Get latest entry for each chain
    return groupedByChain
  }, [timeseriesData])

  const chainItems = useMemo(() => {
    if (!project) return []

    const chainIds = new Set(
      timeseriesData?.sorted_data_ids
        .map(({ chain_id }) => chain_id)
        .filter(Boolean)
    ) as Set<string>

    project.chains.forEach((chain) => {
      chainIds.add(chain)
    })

    const filteredChainIds = Array.from(chainIds).filter((chain_id) => {
      if (shouldFilterSelf) {
        return chain_id !== project.data_id
      }
      return true
    })

    return (filteredChainIds
      .map((chain_id) => {
        const chain = chainsById.get(chain_id!)

        if (!chain) {
          return null
        }

        return {
          logo: chain?.logo,
          name: chain?.name || chain_id,
          slug: chain?.project_slug,
          chain_id,
          value: latestTimeseriesData[chain_id!]?.value || null,
        }
      })
      .filter(Boolean) || []) as ChainItem[]
  }, [
    project,
    timeseriesData?.sorted_data_ids,
    shouldFilterSelf,
    chainsById,
    latestTimeseriesData,
  ])

  if (!project || chainItems?.length === 0) {
    return null
  }

  return (
    <Modal isOpen={!!project} onOpenChange={handleClose}>
      <Modal.Header
        title={`${project.name} is deployed on ${chainItems?.length} chain${chainItems?.length === 1 ? "" : "s"}`}
      />
      <Modal.Body>
        <div>
          <div className="flex justify-between mb-2 px-0 text-fg-secondary text-sm">
            <div>Chains</div>
            {metricId && <div>Active users last 7 days</div>}
          </div>
          <div className="flex flex-col border border-solid border-border-default rounded-lg overflow-hidden">
            {chainItems.map((chain) => (
              <Link
                key={chain.name}
                href={`/projects/${chain.slug}`}
                className="flex justify-between items-center gap-2 py-2.5 px-3 hover:bg-neutral-950/5 dark:hover:bg-neutral-50/5 border-b border-solid border-border-default last:border-b-0"
              >
                <div className="flex items-center gap-2">
                  <Avatar
                    src={`https://static1.tokenterminal.com/${chain.logo}`}
                    className="w-4 h-4 rounded-full overflow-hidden shrink-0"
                  />
                  {chain.name}
                </div>
                {metricId && (
                  <div>
                    {chain.value ? (
                      <div className="uppercase">
                        {formatNumber({
                          value: chain.value ?? 0,
                          format: "compact",
                        })}
                      </div>
                    ) : (
                      <div className="">N/A</div>
                    )}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-end">
        <Button onPress={handleClose}>Done</Button>
      </Modal.Footer>
    </Modal>
  )
}
